import React, { lazy, Suspense, useRef, useLayoutEffect, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LoadingSpinner } from './components/Loading/LoadingSpinner';
import { FullPageLoader } from './components/FullPageLoader/FullPageLoader';
import { Navigation } from './components/Navigation/Navigation';
import { PortalPageFooter } from './components/PortalPageFooter/PortalPageFooter';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { AuthErrorBoundary } from './components/AuthErrorBoundary/AuthErrorBoundary';
import { AuthProgressOverlay } from './components/AuthProgressOverlay/AuthProgressOverlay';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ActiveRoleProvider } from './context/ActiveRoleContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { ApprovalsPushProvider } from './context/ApprovalsPushContext';
import { MsalRedirectHandler } from './components/MsalRedirectHandler/MsalRedirectHandler';
import { ScrollToTop } from './components/ScrollToTop/ScrollToTop';
import {
  ClinicStaffApprovalProvider,
  ClinicStaffApprovalLayoutGate,
} from './context/ClinicStaffApprovalContext';
import { AUTH_API_CONFIG } from './config/api.config';
import { registerStorageTransferListener } from './utils/caseMgmtPortal.utils';
import { registerFormEngineStorageTransferListener } from './utils/formEnginePortal.utils';
import { SERVICE_MEMBER_ROUTE } from './constants/serviceMemberRouteConstants';
import { PART_C_PROVIDER_ROUTE } from './constants/partCProviderRouteConstants';
import { REVIEWER_WORKFLOW_ROUTE } from './constants/reviewerWorkflowRouteConstants';
import {
  COMMANDER_READINESS_ROUTE,
  COMMANDER_READINESS_SUMMARY_ROUTE,
} from './constants/commanderReadinessRouteConstants';
import { resolveDocumentTitleForPath } from './utils/portalDocumentTitle.utils';
import { isPhaWorkEnabled } from './utils/examPreviewPortalAccess.utils';
import { PortalProductName } from './constants/portalBrandingConstants';
import { DEFAULT_STALE_TIME_MS } from './config/time.config';
import { FormEngineRedirect } from './components/FormEngineRedirect/FormEngineRedirect';
import { CaseMgmtRedirect } from './components/CaseMgmtRedirect/CaseMgmtRedirect';
import { SessionWarningModal } from './components/SessionWarningModal/SessionWarningModal';
import {
  HSUS_SESSION_EXPIRY_LOCATION_STATE_KEY,
  SessionEvent,
  SessionLogoutReason,
  armSessionTimeoutRedirectFromReason,
  resolveSessionTimeoutNavigationReason,
  getSessionExpiryPreNavigationDelayMs,
  consumePendingSessionTimeoutRedirect,
  targetToPath,
  hasPriorPortalAuthentication,
} from '@hsus-dev/session-policy';
import type { LogoutTriggeredDetail } from '@hsus-dev/session-policy';
import { useAuth } from './context/AuthContext';
import './App.css';
import './styles/sessionExpiredPortalBridge.css';
import { SessionExpiredRoute } from './components/SessionExpiredRoute/SessionExpiredRoute';
import { getMsalInstance } from './services/msalBootstrap.service';
import { isBrowserMsalOAuthRedirect } from './utils/msalOAuthRedirect.utils';
import { hasEmptyMsalAccountCache } from './utils/msalAccountPresence.utils';

// Route-level code splitting: lazy-load pages to keep main chunk under 600 kB
const Home = lazy(() => import('./pages/Home/Home').then((m) => ({ default: m.Home })));
const Login = lazy(() => import('./pages/Login/Login').then((m) => ({ default: m.Login })));
const SignIn = lazy(() => import('./pages/SignIn/SignIn').then((m) => ({ default: m.SignIn })));
const RoleSelection = lazy(() => import('./pages/RoleSelection/RoleSelection').then((m) => ({ default: m.RoleSelection })));
const Registration = lazy(() => import('./pages/Registration/Registration').then((m) => ({ default: m.Registration })));
const NpiValidation = lazy(() => import('./pages/NpiValidation/NpiValidation').then((m) => ({ default: m.NpiValidation })));
const VerifyProviderNpi = lazy(() => import('./pages/VerifyProviderNpi/VerifyProviderNpi').then((m) => ({ default: m.VerifyProviderNpi })));
const Demographics = lazy(() => import('./pages/Demographics/Demographics').then((m) => ({ default: m.Demographics })));
const ProviderLicense = lazy(() => import('./pages/ProviderLicense/ProviderLicense').then((m) => ({ default: m.ProviderLicense })));
const CompleteLicense = lazy(() => import('./pages/CompleteLicense/CompleteLicense').then((m) => ({ default: m.CompleteLicense })));
const AccountSetup = lazy(() => import('./pages/AccountSetup/AccountSetup').then((m) => ({ default: m.AccountSetup })));
const CustomAccountSetup = lazy(() => import('./pages/CustomAccountSetup/CustomAccountSetup').then((m) => ({ default: m.CustomAccountSetup })));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard').then((m) => ({ default: m.Dashboard })));
const Exams = lazy(() => import('./pages/Exams/Exams').then((m) => ({ default: m.Exams })));
const Profile = lazy(() => import('./pages/Profile/Profile').then((m) => ({ default: m.Profile })));
const Unauthorized = lazy(() => import('./pages/Unauthorized/Unauthorized').then((m) => ({ default: m.Unauthorized })));
const NotFound = lazy(() => import('./pages/NotFound/NotFound').then((m) => ({ default: m.NotFound })));
const LoginErrorPage = lazy(() => import('./pages/LoginError/LoginError').then((m) => ({ default: m.LoginErrorPage })));
const ProviderApprovals = lazy(() => import('./pages/ProviderApprovals/ProviderApprovals').then((m) => ({ default: m.ProviderApprovals })));
const ApplicantDetail = lazy(() => import('./pages/ProviderApprovals/ApplicantDetail').then((m) => ({ default: m.ApplicantDetail })));
const ClinicStaffPendingApproval = lazy(() => import('./pages/ClinicStaffPendingApproval/ClinicStaffPendingApproval').then((m) => ({ default: m.ClinicStaffPendingApproval })));
const RegistrationPendingApproval = lazy(() => import('./pages/RegistrationPendingApproval/RegistrationPendingApproval').then((m) => ({ default: m.RegistrationPendingApproval })));
const WorkflowQueueHostPage = lazy(() => import('./pages/WorkflowQueue/WorkflowQueueHostPage'));
const PhaPortalHostPage = lazy(() => import('./pages/PhaPortal/PhaPortalHostPage'));
const PartCProviderHostPage = lazy(() => import('./pages/PartCProvider/PartCProviderHostPage'));
const ReviewerWorkflowHostPage = lazy(() => import('./pages/ReviewerWorkflow/ReviewerWorkflowHostPage'));
const CommanderReadinessHostPage = lazy(() => import('./pages/CommanderReadiness/CommanderReadinessHostPage'));
const CommanderReadinessSummaryHostPage = lazy(
  () => import('./pages/CommanderReadiness/CommanderReadinessSummaryHostPage')
);

/** Old /pha bookmarks → /service-member (keeps query string). */
function PhaToServiceMemberRedirect() {
  const location = useLocation();
  return <Navigate to={`${SERVICE_MEMBER_ROUTE}${location.search}${location.hash}`} replace />;
}
const LegalPrivacyPage = lazy(() =>
  import('./pages/Legal/LegalPrivacyPage').then((m) => ({ default: m.LegalPrivacyPage })),
);
const LegalTermsPage = lazy(() =>
  import('./pages/Legal/LegalTermsPage').then((m) => ({ default: m.LegalTermsPage })),
);
// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 3,
      staleTime: DEFAULT_STALE_TIME_MS,
    },
  },
});

/**
 * Public paths that do not require portal_authenticated (login, signin, registration, etc.).
 * When adding new public routes, add them here or extend the check in isPublicPath.
 * Note: Forgot-password is handled by Azure/IdP, not in-app.
 */
const PUBLIC_PATHS = [
  '/',
  '/login',
  '/signin',
  '/login-error',
  '/session-expired',
  '/choose-role',
  '/404',
  '/unauthorized',
];

function isPublicPath(pathname: string): boolean {
  if (PUBLIC_PATHS.includes(pathname)) return true;
  if (pathname.startsWith('/registration')) return true;
  if (pathname.startsWith('/legal')) return true;
  return false;
}

/** Hide-then-reveal delay (matches ThemeContext: double rAF + ~1 frame). */
const REVEAL_DELAY_MS = 16;

/**
 * View shown only during portal_validating. Adds theme-transitioning so only
 * the full-page loader is visible (no flicker); loader container is revealed via CSS.
 */
const PortalValidatingView: React.FC = () => {
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add('theme-transitioning');
    return () => {
      root.classList.remove('theme-transitioning');
    };
  }, []);
  return <FullPageLoader />;
};

/**
 * Top-level route guard: render based on portalAuthState.
 * - portal_validating: full-page loader only (hide-then-reveal, no header/nav).
 * - portal_error: single redirect to /login-error (replace), then render when on /login-error.
 * - not portal_authenticated: show loader while isLoading (auth restoring from storage), then redirect to /login unless on a public path. This prevents new-tab opens (e.g. /dashboard) from flashing to /login then back to dashboard.
 * - portal_authenticated: render Dashboard and protected routes (and nav).
 */
const PortalAuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState, isLoading } = useAuth();
  const location = useLocation();
  const prevAuthStateRef = useRef(authState);
  const previousAuthState = prevAuthStateRef.current;
  prevAuthStateRef.current = authState;

  useEffect(() => {
    if (previousAuthState !== 'portal_validating' || authState === 'portal_validating') return;
    const root = document.documentElement;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          root.classList.remove('theme-transitioning');
        }, REVEAL_DELAY_MS);
      });
    });
  }, [authState, previousAuthState]);

  // Single return at end so the same number of hooks run every render (Rules of Hooks; Context7: call hooks at top level, before any early returns).
  let content: React.ReactNode;
  if (authState === 'portal_validating') {
    content = <PortalValidatingView />;
  } else if (authState === 'portal_error') {
    if (location.pathname !== '/login-error') {
      content = <Navigate to="/login-error" replace />;
    } else {
      content = <>{children}</>;
    }
  } else if (authState === 'portal_authenticated') {
    // Retry succeeded while on /login-error — redirect to dashboard now that auth is confirmed.
    if (location.pathname === '/login-error') {
      content = <Navigate to="/dashboard" replace />;
    } else {
      content = <>{children}</>;
    }
  } else {
    // Wait for initial auth restore from storage before redirecting to login (fixes new-tab: open /dashboard -> flash to /login -> then to dashboard)
    if (isLoading) {
      content = <PortalValidatingView />;
    } else {
      const consumed = consumePendingSessionTimeoutRedirect();
      if (consumed !== null) {
        const path = targetToPath(consumed.target);
        // If navigate() in the LogoutTriggered listener already updated the URL, skip a
        // second <Navigate> to avoid redundant history writes / double navigation.
        content =
          location.pathname === path ? (
            <>{children}</>
          ) : (
            <Navigate
              to={path}
              replace
              state={
                path === '/session-expired'
                  ? { [HSUS_SESSION_EXPIRY_LOCATION_STATE_KEY]: consumed.expiryKind }
                  : undefined
              }
            />
          );
      } else {
        const shouldGoToLogin =
          location.pathname === '/login-error' || !isPublicPath(location.pathname);
        if (!shouldGoToLogin) {
          content = <>{children}</>;
        } else if (
          AUTH_API_CONFIG.providerType === 'msal' &&
          isBrowserMsalOAuthRedirect()
        ) {
          // Let MsalRedirectHandler process ?code= / hash before sending user away.
          content = <>{children}</>;
        } else {
          const msalInstance = getMsalInstance();
          const emptyLocalMsalAccountCache =
            msalInstance !== null &&
            hasEmptyMsalAccountCache(msalInstance) &&
            hasPriorPortalAuthentication();
          content = emptyLocalMsalAccountCache ? (
            <Navigate
              to="/session-expired"
              replace
              state={{ [HSUS_SESSION_EXPIRY_LOCATION_STATE_KEY]: 'idle' }}
            />
          ) : (
            <Navigate to="/login" replace />
          );
        }
      }
    }
  }
  return content;
};

function getPortalChromePathFlags(pathname: string) {
  // PHA paths drop portal chrome only while the work is on; paused, they show Not Found inside it.
  const pha = isPhaWorkEnabled();
  return {
    isLoginOrSignIn: pathname === '/login' || pathname === '/signin',
    isSessionExpiredPage: pathname === '/session-expired',
    isExamFormPage: pathname === '/examform',
    isLoginErrorPage: pathname === '/login-error',
    isClinicStaffPendingApprovalPage: pathname === '/clinic-staff-pending-approval',
    isRegistrationPendingApprovalPage: pathname === '/registration/pending-approval',
    isHomePage: pathname === '/',
    isWorkflowQueuePage:
      pha && (pathname === '/workflow-queue' || pathname.startsWith('/workflow-queue/')),
    isPhaPortalPage:
      pha &&
      (pathname === SERVICE_MEMBER_ROUTE ||
        pathname.startsWith(`${SERVICE_MEMBER_ROUTE}/`) ||
        pathname === '/pha' ||
        pathname.startsWith('/pha/')),
    isPartCProviderPage:
      pha &&
      (pathname === PART_C_PROVIDER_ROUTE || pathname.startsWith(`${PART_C_PROVIDER_ROUTE}/`)),
    isReviewerWorkflowPage:
      pha &&
      (pathname === REVIEWER_WORKFLOW_ROUTE || pathname.startsWith(`${REVIEWER_WORKFLOW_ROUTE}/`)),
    isCommanderReadinessPage:
      pha &&
      (pathname === COMMANDER_READINESS_ROUTE ||
        pathname.startsWith(`${COMMANDER_READINESS_ROUTE}/`)),
    isCommanderReadinessSummaryPage:
      pha &&
      (pathname === COMMANDER_READINESS_SUMMARY_ROUTE ||
        pathname.startsWith(`${COMMANDER_READINESS_SUMMARY_ROUTE}/`)),
  };
}

/**
 * Main nav — hidden on auth/login layouts and on unauthenticated Home (`/`).
 * Unauthenticated Home uses hero CTAs; sticky page header on scroll when unauthenticated — see docs/NAV_UNAUTHENTICATED_SIGN_IN.md.
 */
const PortalNavigation: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const flags = getPortalChromePathFlags(location.pathname);

  const showNavigation =
    !flags.isLoginOrSignIn &&
    !flags.isSessionExpiredPage &&
    !flags.isExamFormPage &&
    !flags.isLoginErrorPage &&
    !flags.isClinicStaffPendingApprovalPage &&
    !flags.isRegistrationPendingApprovalPage &&
    !flags.isWorkflowQueuePage &&
    !flags.isPhaPortalPage &&
    !flags.isPartCProviderPage &&
    !flags.isReviewerWorkflowPage &&
    !flags.isCommanderReadinessPage &&
    !flags.isCommanderReadinessSummaryPage &&
    !(flags.isHomePage && !isAuthenticated);

  if (!showNavigation) return null;
  return <Navigation />;
};

export const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const flags = getPortalChromePathFlags(location.pathname);
  const showPortalChrome =
    !flags.isLoginOrSignIn &&
    !flags.isSessionExpiredPage &&
    !flags.isExamFormPage &&
    !flags.isLoginErrorPage &&
    !flags.isClinicStaffPendingApprovalPage &&
    !flags.isRegistrationPendingApprovalPage &&
    !flags.isWorkflowQueuePage &&
    !flags.isPhaPortalPage &&
    !flags.isPartCProviderPage &&
    !flags.isReviewerWorkflowPage &&
    !flags.isCommanderReadinessPage &&
    !flags.isCommanderReadinessSummaryPage;

  useEffect(() => {
    const unsubscribeCaseMgmt = registerStorageTransferListener();
    const unsubscribeFormEngine = registerFormEngineStorageTransferListener();
    return () => {
      unsubscribeCaseMgmt();
      unsubscribeFormEngine();
    };
  }, []);

  useEffect(() => {
    // Paused PHA paths render Not Found, so they keep the provider title.
    document.title = isPhaWorkEnabled()
      ? resolveDocumentTitleForPath(location.pathname)
      : PortalProductName.PROVIDER;
  }, [location.pathname]);

  // Idle timeout → /session-expired. Absolute timeout → /login (per product policy).
  // Cross-tab: resolveSessionTimeoutNavigationReason (underlying idle/absolute, else idle).
  // User-initiated logout (reason = "user") does not use this path.
  //
  // WHY module-level state instead of sessionStorage:
  // AuthContext.logout() calls clearAllSessionStorage() synchronously in the same tick as
  // setAuthState('unauthenticated'), before React processes the queued state update and
  // re-renders PortalAuthGuard. Any sessionStorage key set here would be erased by that
  // clearAllSessionStorage() call before the guard can read it.
  useEffect(() => {
    let pendingNavigation: number | undefined;

    const handleLogout = (e: Event) => {
      const detail = (e as CustomEvent<LogoutTriggeredDetail>).detail;
      const reason = detail?.reason;
      const underlyingReason = detail?.underlyingReason;
      const navigationReason = resolveSessionTimeoutNavigationReason(reason, underlyingReason);
      const path = armSessionTimeoutRedirectFromReason(navigationReason);
      if (path !== null) {
        if (pendingNavigation !== undefined) {
          window.clearTimeout(pendingNavigation);
        }
        const delayMs = getSessionExpiryPreNavigationDelayMs();
        pendingNavigation = window.setTimeout(() => {
          pendingNavigation = undefined;
          navigate(path, {
            replace: true,
            state:
              path === '/session-expired'
                ? {
                    [HSUS_SESSION_EXPIRY_LOCATION_STATE_KEY]:
                      navigationReason === SessionLogoutReason.Absolute ? 'absolute' : 'idle',
                  }
                : undefined,
          });
        }, delayMs);
      }
    };
    window.addEventListener(SessionEvent.LogoutTriggered, handleLogout);
    return () => {
      window.removeEventListener(SessionEvent.LogoutTriggered, handleLogout);
      if (pendingNavigation !== undefined) {
        window.clearTimeout(pendingNavigation);
      }
    };
  }, [navigate]);

  return (
    <AuthProvider>
      <ThemeProvider>
        <ActiveRoleProvider>
              <QueryClientProvider client={queryClient}>
                <PermissionsProvider>
                  <ErrorBoundary>
                    <div className="app">
                    {/* MsalRedirectHandler must be inside AuthProvider (AuthProvider wraps app content in App.tsx) */}
                    {/* Wrap with AuthErrorBoundary for additional safety (CODE_REVIEW_CONTEXT7.md recommendation) */}
                    {AUTH_API_CONFIG.providerType === 'msal' && (
                      <AuthErrorBoundary>
                        <React.Suspense fallback={null}>
                          <MsalRedirectHandler />
                        </React.Suspense>
                      </AuthErrorBoundary>
                    )}
                    <div className="flex min-h-0 flex-1 flex-col">
                    <PortalAuthGuard>
                      <ApprovalsPushProvider>
                      <div className="flex min-h-0 w-full flex-1 flex-col">
                      <ClinicStaffApprovalProvider>
                      <ClinicStaffApprovalLayoutGate>
                      <ScrollToTop />
                      <AuthProgressOverlay />
                      <SessionWarningModal />
                      {/* Hide main nav on login/signin/session-expired/login-error/clinic-staff-pending-approval; hide on unauthenticated Home */}
                      <PortalNavigation />
                      <main className="flex min-h-0 w-full flex-1 flex-col" id="main-content">
                      <div className="flex min-h-0 w-full flex-1 flex-col">
                      <Suspense
                        fallback={
                          <LoadingSpinner message="Loading page…" ariaLabel="Loading page" />
                        }
                      >
                        <Routes>
                          {/* Public routes */}
                          <Route path="/" element={<Home />} />

                          {/* Sign in route - redirects to dashboard if already authenticated */}
                          <Route
                            path="/login"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Login />
                    </ProtectedRoute>
                  }
                />

                          {/* Alternate sign in route - tabbed interface for sign in/register */}
                          <Route
                            path="/signin"
                            element={
                              <ProtectedRoute requireAuth={false}>
                                <SignIn />
                              </ProtectedRoute>
                            }
                          />

                          {/* Login error page - shown when portalAuthState === 'portal_error' */}
                          <Route path="/login-error" element={<LoginErrorPage />} />

                          {/* Session expired page - idle timeout only; absolute → /login (@hsus-dev/session-policy sessionTimeoutNavigation, AUTH_MENTAL_MODEL §5.1) */}
                          <Route
                            path="/session-expired"
                            element={
                              <div className="hcp-session-expired-bridge">
                                <SessionExpiredRoute loginPath="/login" portalName="Portal" />
                              </div>
                            }
                          />

                          {/* Clinic staff pending provider approval - only message, no app access */}
                          <Route
                            path="/clinic-staff-pending-approval"
                            element={
                              <ProtectedRoute>
                                <ClinicStaffPendingApproval />
                              </ProtectedRoute>
                            }
                          />

                          {/* Role selection route - part of registration flow */}
                <Route
                  path="/choose-role"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <RoleSelection />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/legal/privacy-policy"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <LegalPrivacyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/legal/terms-and-conditions"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <LegalTermsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Registration route - part of registration flow */}
                <Route
                  path="/registration"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Registration />
                    </ProtectedRoute>
                  }
                />

                {/* NPI Validation route - step 1 of registration flow (Provider) */}
                <Route
                  path="/registration/npi-validation"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <NpiValidation />
                    </ProtectedRoute>
                  }
                />

                {/* Verify Provider's NPI - step 1 for Office Admin / Pre-Exam Preparer */}
                <Route
                  path="/registration/verify-provider-npi"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <VerifyProviderNpi />
                    </ProtectedRoute>
                  }
                />

                {/* Demographics route - step 2 of registration flow */}
                <Route
                  path="/registration/demographics"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Demographics />
                    </ProtectedRoute>
                  }
                />

                {/* Provider License route - step 3 of registration flow (SignIn > Create Account) */}
                <Route
                  path="/registration/provider-license"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <ProviderLicense />
                    </ProtectedRoute>
                  }
                />

                {/* Pending approval – shown after successful registration (user not logged in) */}
                <Route
                  path="/registration/pending-approval"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <RegistrationPendingApproval />
                    </ProtectedRoute>
                  }
                />

                {/* Account Setup route - step 3 of registration flow */}
                <Route
                  path="/registration/account-setup"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <AccountSetup />
                    </ProtectedRoute>
                  }
                />

                {/* Custom Account Setup route - step 3b of registration flow */}
                <Route
                  path="/registration/custom-account-setup"
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <CustomAccountSetup />
                    </ProtectedRoute>
                  }
                />

                {/* Complete License route - required if skipped during registration */}
                <Route
                  path="/complete-license"
                  element={
                    <ProtectedRoute>
                      <CompleteLicense />
                    </ProtectedRoute>
                  }
                />

                {/* Edit License & Credentials route - from Profile page */}
                <Route
                  path="/profile/edit-credentials"
                  element={
                    <ProtectedRoute>
                      <CompleteLicense isEditMode={true} />
                    </ProtectedRoute>
                  }
                />

                {/* Protected routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exams"
                  element={
                    <ProtectedRoute>
                      <Exams />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/provider-approvals"
                  element={
                    <ProtectedRoute>
                      <ProviderApprovals />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/provider-approvals/applicant/:id"
                  element={
                    <ProtectedRoute>
                      <ApplicantDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                {/* Form routes: always redirect to Form Engine Portal (FEP) */}
                <Route path="/form/:formId" element={<ProtectedRoute><FormEngineRedirect /></ProtectedRoute>} />
                <Route path="/examform" element={<ProtectedRoute><FormEngineRedirect /></ProtectedRoute>} />
                <Route path="/view" element={<ProtectedRoute><FormEngineRedirect /></ProtectedRoute>} />
                {/* Case management routes: open or reuse named Case Management Portal tab */}
                <Route path="/case-management/*" element={<ProtectedRoute><CaseMgmtRedirect /></ProtectedRoute>} />

                {/* PHA / Workflow Queue — require portal login like other protected pages.
                    Paused unless VITE_ENABLE_PHA_WORK=true; when off these paths hit Not Found. */}
                {isPhaWorkEnabled() && (
                  <>
                    <Route
                      path="/workflow-queue/*"
                      element={
                        <ProtectedRoute>
                          <WorkflowQueueHostPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${SERVICE_MEMBER_ROUTE}/*`}
                      element={
                        <ProtectedRoute>
                          <PhaPortalHostPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/pha/*"
                      element={
                        <ProtectedRoute>
                          <PhaToServiceMemberRedirect />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${PART_C_PROVIDER_ROUTE}/*`}
                      element={
                        <ProtectedRoute>
                          <PartCProviderHostPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${REVIEWER_WORKFLOW_ROUTE}/*`}
                      element={
                        <ProtectedRoute>
                          <ReviewerWorkflowHostPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${COMMANDER_READINESS_ROUTE}/*`}
                      element={
                        <ProtectedRoute>
                          <CommanderReadinessHostPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${COMMANDER_READINESS_SUMMARY_ROUTE}/*`}
                      element={
                        <ProtectedRoute>
                          <CommanderReadinessSummaryHostPage />
                        </ProtectedRoute>
                      }
                    />
                  </>
                )}

                          {/* Error pages */}
                          <Route path="/unauthorized" element={<Unauthorized />} />
                          <Route path="/404" element={<NotFound />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                      </div>
                      </main>
                      {showPortalChrome && <PortalPageFooter />}
                      </ClinicStaffApprovalLayoutGate>
                      </ClinicStaffApprovalProvider>
                      </div>
                      </ApprovalsPushProvider>
                    </PortalAuthGuard>
                    </div>
                  </div>
                  </ErrorBoundary>
                </PermissionsProvider>
              </QueryClientProvider>
          </ActiveRoleProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

